import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '../i18n';
import { quoteOfTheDay } from '../data/dailyQuotes';
import { shareDailyCard } from '../lib/dailyCardImage';
import { getVisitorId } from '../lib/visitorId';

interface Props {
  restaurantId: string;
  restaurantName: string;
  logoUrl?: string | null;
  /** Local calendar day (YYYY-MM-DD) — the quote is stable for a restaurant for that day. */
  isoDay: string;
  onClose: () => void;
}

const REVEAL_AT = 0.7;         // share of the scratch surface that must be cleared to count as revealed
const BRUSH = 28;              // brush diameter in CSS px
const CELL = 14;               // coverage grid cell in CSS px

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// The "scratch-off" daily card. Never blocks the menu: Skip is always on screen, and the card
// itself is only shown once per restaurant per day on a device (the caller enforces that).
// Anyone who would rather not scratch — reduced-motion users, keyboard users, or just a
// preference — can reveal it with a tap/Enter.
export default function DailyCardModal({ restaurantId, restaurantName, logoUrl, isoDay, onClose }: Props) {
  const { lang, t } = useLanguage();
  const visitorId = useRef(getVisitorId()).current;
  const quote = quoteOfTheDay(restaurantId, isoDay, visitorId);
  const text = lang === 'sq' ? quote.sq : quote.en;

  const reduced = useRef(prefersReducedMotion()).current;
  const [revealed, setRevealed] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const grid = useRef<{ cols: number; rows: number; cells: Uint8Array; hit: number } | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  const reveal = useCallback(() => setRevealed(true), []);
  const [sharing, setSharing] = useState<'idle' | 'busy' | 'saved'>('idle');

  // Paint the silver scratch surface at the real pixel size (retina-aware) and reset coverage.
  const paintSurface = useCallback(() => {
    const wrap = wrapRef.current, canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const w = wrap.clientWidth, h = wrap.clientHeight;
    if (!w || !h) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, '#7C2D43'); g.addColorStop(0.35, '#A24960'); g.addColorStop(0.62, '#6B2737'); g.addColorStop(1, '#4E1725');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    // fine speckle so it reads as a foil, not a flat grey
    for (let i = 0; i < 260; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? 'rgba(255,255,255,.22)' : 'rgba(0,0,0,.14)';
      ctx.fillRect(Math.random() * w, Math.random() * h, 1.6, 1.6);
    }
    // The foil carries the Assaggioo mark + name (same geometry as AssaggiooLogo.tsx) — that is
    // what the guest scratches away. A soft shadow under each shape reads as embossed foil.
    const mark = Math.min(h * 0.4, w * 0.28);
    const mx = w / 2 - mark / 2, my = h * 0.1;
    const u = mark / 100;
    const leg = (x1: number, y1: number, x2: number, y2: number) => {
      ctx.lineCap = 'round'; ctx.lineWidth = 15 * u;
      ctx.strokeStyle = 'rgba(0,0,0,.35)';
      ctx.beginPath(); ctx.moveTo(mx + x1 * u, my + y1 * u + 2); ctx.lineTo(mx + x2 * u, my + y2 * u + 2); ctx.stroke();
      ctx.strokeStyle = '#FFFFFF';
      ctx.beginPath(); ctx.moveTo(mx + x1 * u, my + y1 * u); ctx.lineTo(mx + x2 * u, my + y2 * u); ctx.stroke();
    };
    leg(50, 12, 15, 90); leg(50, 12, 85, 90);
    const bar = (fill: string, dy: number) => {
      const bx = mx + 29 * u, by = my + 57 * u + dy, bw = 42 * u, bh = 13 * u, r = bh / 2;
      ctx.fillStyle = fill; ctx.beginPath();
      ctx.moveTo(bx + r, by); ctx.lineTo(bx + bw - r, by); ctx.arc(bx + bw - r, by + r, r, -Math.PI / 2, Math.PI / 2);
      ctx.lineTo(bx + r, by + bh); ctx.arc(bx + r, by + r, r, Math.PI / 2, -Math.PI / 2); ctx.closePath(); ctx.fill();
    };
    bar('rgba(0,0,0,.35)', 2); bar('#F59E0B', 0);
    ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
    const nameSize = Math.max(22, Math.min(34, w / 9));
    ctx.font = `400 ${nameSize}px 'DM Serif Display', Georgia, serif`;
    const ny = my + mark + nameSize + 8;
    ctx.fillStyle = 'rgba(0,0,0,.35)'; ctx.fillText('Assaggioo', w / 2, ny + 2);
    ctx.fillStyle = '#FBEFF2'; ctx.fillText('Assaggioo', w / 2, ny);
    ctx.fillStyle = 'rgba(255,255,255,.8)';
    ctx.font = `700 ${Math.max(11, Math.min(13, w / 28))}px 'DM Sans', system-ui, sans-serif`;
    ctx.fillText(t.daily_card_scratch_hint.toUpperCase(), w / 2, h - 16);
    const cols = Math.ceil(w / CELL), rows = Math.ceil(h / CELL);
    grid.current = { cols, rows, cells: new Uint8Array(cols * rows), hit: 0 };
  }, [t.daily_card_scratch_hint]);

  useEffect(() => {
    if (reduced || revealed) return;
    // The name on the foil is set in the display face — paint after it is available so the
    // canvas doesn't lock in a fallback font (canvas text never re-renders on its own).
    paintSurface();
    try { document.fonts?.load("34px 'DM Serif Display'").then(() => { if (!drawing.current) paintSurface(); }); } catch { /* ignore */ }
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return;
    let first = true;
    const ro = new ResizeObserver(() => { if (first) { first = false; return; } if (!drawing.current) paintSurface(); });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [reduced, revealed, paintSurface]);

  // Escape = skip, so a keyboard user is never stuck.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const local = (e: React.PointerEvent) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const markCoverage = (x: number, y: number) => {
    const gr = grid.current;
    if (!gr) return;
    const r = BRUSH / 2;
    const c0 = Math.max(0, Math.floor((x - r) / CELL)), c1 = Math.min(gr.cols - 1, Math.floor((x + r) / CELL));
    const r0 = Math.max(0, Math.floor((y - r) / CELL)), r1 = Math.min(gr.rows - 1, Math.floor((y + r) / CELL));
    for (let cy = r0; cy <= r1; cy++) for (let cx = c0; cx <= c1; cx++) {
      const i = cy * gr.cols + cx;
      if (!gr.cells[i]) { gr.cells[i] = 1; gr.hit++; }
    }
    if (gr.hit / (gr.cols * gr.rows) >= REVEAL_AT) reveal();
  };

  const stroke = (x: number, y: number) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.lineWidth = BRUSH;
    ctx.beginPath();
    const from = last.current || { x, y };
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    last.current = { x, y };
    markCoverage(x, y);
  };

  const onDown = (e: React.PointerEvent) => {
    drawing.current = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const p = local(e); last.current = null; stroke(p.x, p.y);
  };
  const onMove = (e: React.PointerEvent) => { if (drawing.current) { const p = local(e); stroke(p.x, p.y); } };
  const onUp = () => { drawing.current = false; last.current = null; };

  const dateLabel = new Date().toLocaleDateString(lang === 'sq' ? 'sq-AL' : 'en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  const onShare = async () => {
    setSharing('busy');
    const res = await shareDailyCard({ quote: text, restaurantName, title: t.daily_card_title, dateLabel, url: window.location.origin + window.location.pathname });
    setSharing(res === 'downloaded' ? 'saved' : 'idle');
  };

  return (
    <div className="dc-overlay" role="dialog" aria-modal="true" aria-label={t.daily_card_title}>
      <div className="dc-box">
        <button className="dc-x" onClick={onClose} aria-label={t.daily_card_close}>
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
        </button>

        <div className="dc-head">
          {logoUrl
            ? <img className="dc-logo" src={logoUrl} alt="" />
            : <div className="dc-logo dc-logo-fallback">{restaurantName.slice(0, 1).toUpperCase()}</div>}
          <div>
            <div className="dc-eyebrow">{t.daily_card_title}</div>
            <div className="dc-name">{restaurantName}</div>
          </div>
        </div>
        <div className="dc-date">{dateLabel}</div>

        <div className="dc-scratch" ref={wrapRef}>
          <blockquote className={`dc-quote${revealed ? ' shown' : ''}`} aria-live="polite">
            <span>{text}</span>
          </blockquote>
          {!reduced && !revealed && (
            <canvas
              ref={canvasRef}
              className="dc-canvas"
              role="button"
              tabIndex={0}
              aria-label={`${t.daily_card_scratch_hint}. ${t.daily_card_reveal}`}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); reveal(); } }}
            />
          )}
          {reduced && !revealed && (
            <button className="dc-cover-btn" onClick={reveal}>{t.daily_card_reveal}</button>
          )}
        </div>

        {revealed && (
          <button
            className="dc-share"
            onClick={onShare}
            disabled={sharing === 'busy'}
            aria-label={sharing === 'saved' ? t.daily_card_saved : t.daily_card_share}
            title={sharing === 'saved' ? t.daily_card_saved : t.daily_card_share}
            autoFocus
          >
            {sharing === 'saved' ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="18" cy="5" r="2.6" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="6" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="18" cy="19" r="2.6" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8.3 10.8l7.4-4.4M8.3 13.2l7.4 4.4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
